// Canonical mapping schema:
// role/chain definitions and shared constraints used by recognition and mapping I/O.
// =============================================================================
// Roles
// =============================================================================

import {
  type ChainSolverProperties,
  getEffectiveSolverProperties,
  getEffectiveSolverType,
} from './solver';
import { APPENDAGE_HINTS, FINGER_ROLE_NAMES } from './namingCatalog';

export {
  APPENDAGE_HINTS,
  FINGER_ROLE_NAMES,
  NON_THUMB_FINGER_ROLE_NAMES,
  ROLE_HINTS,
  ROLE_TERMS,
  isAnimationRootLikeName,
  SIDE_CONVENTION_PATTERNS,
  SIDE_PATTERNS,
  caseAwarePatternMatch,
  classifyToeParentName,
  collectRoleTerms,
  isContainerLikeName,
  detectSideConvention,
  detectSideFromName,
  detectSideFromNameWithConvention,
  getSemanticNamingPatterns,
  hasRoleTerm,
  isAccessoryBranchLikeName,
  isArmLikeName,
  isClavicleLikeName,
  isDeformationHelperLikeName,
  isFingerHelperLikeName,
  isFootLikeName,
  isGenericNumberedPlaceholderJointName,
  isHandLikeName,
  isHeadLikeName,
  isHeadDecorativeName,
  isHockLikeName,
  isInlineChestLikeName,
  isInlineDeformationLikeName,
  isLegLikeName,
  isMetacarpalLikeName,
  isLowerArmLikeName,
  isLowerLegLikeName,
  isNeckLikeName,
  isPelvisLikeName,
  isPrimaryRootLikeName,
  isPrimarySpineBridgeName,
  isSecondaryRootLikeName,
  isSideHipConnectorName,
  isSimpleHeadSegmentName,
  isSpineLikeName,
  isStructuralArmAuxiliaryName,
  isStructuralArmChainName,
  isStructuralLegAuxiliaryName,
  isTerminalHelperLikeName,
  isToeLikeName,
  isToeRoleCompatibleName,
  isTwistLikeName,
  isUpperArmLikeName,
  isUpperLegChainCandidateName,
  isUpperLegLikeName,
  isLegHipChainName,
  isTorsoAuxiliaryLeafName,
  matchAppendageTypeFromName,
  nameMatchesAnyRole,
  sideConventionDescribe,
  sideConventionDetectSide,
  sideConventionIsValid,
  sideConventionUnknown,
} from './namingCatalog';
export type {
  AppendageType,
  SideConvention,
  SideConventionPattern,
} from './namingCatalog';

export const ROLE_NAMES = [
  "Root",
  "Pelvis",
  "Spine",
  "Chest",
  "Neck",
  "Head",
  "LeftClavicle",
  "LeftUpperArm",
  "LeftLowerArm",
  "LeftHand",
  "RightClavicle",
  "RightUpperArm",
  "RightLowerArm",
  "RightHand",
  "LeftUpperLeg",
  "LeftLowerLeg",
  "LeftHock",
  "LeftFoot",
  "LeftToe",
  "RightUpperLeg",
  "RightLowerLeg",
  "RightHock",
  "RightFoot",
  "RightToe",
  "LeftThumb1", "LeftThumb2", "LeftThumb3",
  "LeftIndexMetacarpal", "LeftIndex1", "LeftIndex2", "LeftIndex3",
  "LeftMiddleMetacarpal", "LeftMiddle1", "LeftMiddle2", "LeftMiddle3",
  "LeftRingMetacarpal", "LeftRing1", "LeftRing2", "LeftRing3",
  "LeftPinkyMetacarpal", "LeftPinky1", "LeftPinky2", "LeftPinky3",
  "RightThumb1", "RightThumb2", "RightThumb3",
  "RightIndexMetacarpal", "RightIndex1", "RightIndex2", "RightIndex3",
  "RightMiddleMetacarpal", "RightMiddle1", "RightMiddle2", "RightMiddle3",
  "RightRingMetacarpal", "RightRing1", "RightRing2", "RightRing3",
  "RightPinkyMetacarpal", "RightPinky1", "RightPinky2", "RightPinky3",
  "LeftClavicle_2",
  "LeftUpperArm_2",
  "LeftLowerArm_2",
  "LeftHand_2",
  "RightClavicle_2",
  "RightUpperArm_2",
  "RightLowerArm_2",
  "RightHand_2",
  "LeftClavicle_3",
  "LeftUpperArm_3",
  "LeftLowerArm_3",
  "LeftHand_3",
  "RightClavicle_3",
  "RightUpperArm_3",
  "RightLowerArm_3",
  "RightHand_3",
  "LeftClavicle_4",
  "LeftUpperArm_4",
  "LeftLowerArm_4",
  "LeftHand_4",
  "RightClavicle_4",
  "RightUpperArm_4",
  "RightLowerArm_4",
  "RightHand_4",
  "LeftClavicle_5",
  "LeftUpperArm_5",
  "LeftLowerArm_5",
  "LeftHand_5",
  "RightClavicle_5",
  "RightUpperArm_5",
  "RightLowerArm_5",
  "RightHand_5",
] as const;

// =============================================================================
// Chains
// =============================================================================

export interface ChainSpec {
  name: string;
  description: string;
  startRole: string;
  endRole: string;
  ikGoal: string | null;
  minBones: number;
  maxBones: number;
  detectionHints: string[];
}

export class ChainMapping {
  name: string;
  startBone: string;
  endBone: string;
  boneNames: string[];
  ikGoal: string | null;
  confidence: number;
  solverType: string | null;
  solverParams: Record<string, unknown>;
  solverProperties: ChainSolverProperties | null;
  legType: string | null;

  constructor(fields: {
    name: string;
    startBone: string;
    endBone: string;
    boneNames?: string[];
    ikGoal?: string | null;
    confidence?: number;
    solverType?: string | null;
    solverParams?: Record<string, unknown>;
    solverProperties?: ChainSolverProperties | null;
    legType?: string | null;
  }) {
    this.name = fields.name;
    this.startBone = fields.startBone;
    this.endBone = fields.endBone;
    this.boneNames = fields.boneNames ?? [];
    this.ikGoal = fields.ikGoal ?? null;
    this.confidence = fields.confidence ?? 1.0;
    this.solverType = fields.solverType ?? null;
    this.solverParams = fields.solverParams ?? {};
    this.solverProperties = fields.solverProperties ?? null;
    this.legType = fields.legType ?? null;
  }

  toDict(): Record<string, unknown> {
    const result: Record<string, unknown> = {
      name: this.name,
      start_bone: this.startBone,
      end_bone: this.endBone,
      bone_count: this.boneNames.length,
      bone_names: this.boneNames,
      ik_goal: this.ikGoal,
      confidence: Math.round(this.confidence * 1000) / 1000,
    };
    if (this.solverType != null) result.solver_type = this.solverType;
    if (Object.keys(this.solverParams).length > 0) result.solver_params = this.solverParams;
    const effectiveSolverType = getEffectiveSolverType(this.name, this.solverType);
    result.solverProps = getEffectiveSolverProperties(
      effectiveSolverType,
      this.solverProperties,
    ).toDict();
    if (this.legType && this.legType !== "plantigrade") result.leg_type = this.legType;
    return result;
  }
}

export const CHAIN_DEFINITIONS: Record<string, ChainSpec> = {
  Root: {
    name: "Root",
    description: "Root/origin bone - anchor for entire skeleton",
    startRole: "Root",
    endRole: "Root",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["root", "origin", "reference"],
  },
  Pelvis: {
    name: "Pelvis",
    description: "Pelvis/hips bone - anchor for legs and spine",
    startRole: "Pelvis",
    endRole: "Pelvis",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["pelvis", "hips", "hip"],
  },
  Spine: {
    name: "Spine",
    description: "Main torso spine from pelvis to chest",
    startRole: "Spine",
    endRole: "Chest",
    ikGoal: null,
    minBones: 1,
    maxBones: 5,
    detectionHints: ["spine", "spn", "back", "torso"],
  },
  Neck: {
    name: "Neck",
    description: "Neck chain from chest to head base",
    startRole: "Neck",
    endRole: "Neck",
    ikGoal: null,
    minBones: 1,
    maxBones: 3,
    detectionHints: ["neck"],
  },
  Head: {
    name: "Head",
    description: "Head bone(s)",
    startRole: "Head",
    endRole: "Head",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["head", "skull"],
  },
  LeftLeg: {
    name: "LeftLeg",
    description: "Left leg from thigh to foot (plantigrade or digitigrade)",
    startRole: "LeftUpperLeg",
    endRole: "LeftFoot",
    ikGoal: "LeftFootIK",
    minBones: 3,
    maxBones: 6,
    detectionHints: ["thigh", "calf", "foot", "toe", "ball", "leg", "hock", "metatars"],
  },
  RightLeg: {
    name: "RightLeg",
    description: "Right leg from thigh to foot (plantigrade or digitigrade)",
    startRole: "RightUpperLeg",
    endRole: "RightFoot",
    ikGoal: "RightFootIK",
    minBones: 3,
    maxBones: 6,
    detectionHints: ["thigh", "calf", "foot", "toe", "ball", "leg", "hock", "metatars"],
  },
  LeftToe: {
    name: "LeftToe",
    description: "Left toe bone",
    startRole: "LeftToe",
    endRole: "LeftToe",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["toe", "toes", "ball", "metatars"],
  },
  RightToe: {
    name: "RightToe",
    description: "Right toe bone",
    startRole: "RightToe",
    endRole: "RightToe",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["toe", "toes", "ball", "metatars"],
  },
  LeftClavicle: {
    name: "LeftClavicle",
    description: "Left shoulder/clavicle bone",
    startRole: "LeftClavicle",
    endRole: "LeftClavicle",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  RightClavicle: {
    name: "RightClavicle",
    description: "Right shoulder/clavicle bone",
    startRole: "RightClavicle",
    endRole: "RightClavicle",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  LeftArm: {
    name: "LeftArm",
    description: "Left arm from upper arm to hand",
    startRole: "LeftUpperArm",
    endRole: "LeftHand",
    ikGoal: "LeftHandIK",
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },
  RightArm: {
    name: "RightArm",
    description: "Right arm from upper arm to hand",
    startRole: "RightUpperArm",
    endRole: "RightHand",
    ikGoal: "RightHandIK",
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },

  // Extra arm chains (set 2)
  LeftClavicle_2: {
    name: "LeftClavicle_2",
    description: "Left clavicle for extra arm set 2",
    startRole: "LeftClavicle_2",
    endRole: "LeftClavicle_2",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  RightClavicle_2: {
    name: "RightClavicle_2",
    description: "Right clavicle for extra arm set 2",
    startRole: "RightClavicle_2",
    endRole: "RightClavicle_2",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  LeftArm_2: {
    name: "LeftArm_2",
    description: "Left arm from upper arm to hand (extra set 2)",
    startRole: "LeftUpperArm_2",
    endRole: "LeftHand_2",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },
  RightArm_2: {
    name: "RightArm_2",
    description: "Right arm from upper arm to hand (extra set 2)",
    startRole: "RightUpperArm_2",
    endRole: "RightHand_2",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },

  // Extra arm chains (set 3)
  LeftClavicle_3: {
    name: "LeftClavicle_3",
    description: "Left clavicle for extra arm set 3",
    startRole: "LeftClavicle_3",
    endRole: "LeftClavicle_3",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  RightClavicle_3: {
    name: "RightClavicle_3",
    description: "Right clavicle for extra arm set 3",
    startRole: "RightClavicle_3",
    endRole: "RightClavicle_3",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  LeftArm_3: {
    name: "LeftArm_3",
    description: "Left arm from upper arm to hand (extra set 3)",
    startRole: "LeftUpperArm_3",
    endRole: "LeftHand_3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },
  RightArm_3: {
    name: "RightArm_3",
    description: "Right arm from upper arm to hand (extra set 3)",
    startRole: "RightUpperArm_3",
    endRole: "RightHand_3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },

  // Extra arm chains (set 4)
  LeftClavicle_4: {
    name: "LeftClavicle_4",
    description: "Left clavicle for extra arm set 4",
    startRole: "LeftClavicle_4",
    endRole: "LeftClavicle_4",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  RightClavicle_4: {
    name: "RightClavicle_4",
    description: "Right clavicle for extra arm set 4",
    startRole: "RightClavicle_4",
    endRole: "RightClavicle_4",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  LeftArm_4: {
    name: "LeftArm_4",
    description: "Left arm from upper arm to hand (extra set 4)",
    startRole: "LeftUpperArm_4",
    endRole: "LeftHand_4",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },
  RightArm_4: {
    name: "RightArm_4",
    description: "Right arm from upper arm to hand (extra set 4)",
    startRole: "RightUpperArm_4",
    endRole: "RightHand_4",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },

  // Extra arm chains (set 5)
  LeftClavicle_5: {
    name: "LeftClavicle_5",
    description: "Left clavicle for extra arm set 5",
    startRole: "LeftClavicle_5",
    endRole: "LeftClavicle_5",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  RightClavicle_5: {
    name: "RightClavicle_5",
    description: "Right clavicle for extra arm set 5",
    startRole: "RightClavicle_5",
    endRole: "RightClavicle_5",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["clavicle", "collar", "shoulder"],
  },
  LeftArm_5: {
    name: "LeftArm_5",
    description: "Left arm from upper arm to hand (extra set 5)",
    startRole: "LeftUpperArm_5",
    endRole: "LeftHand_5",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },
  RightArm_5: {
    name: "RightArm_5",
    description: "Right arm from upper arm to hand (extra set 5)",
    startRole: "RightUpperArm_5",
    endRole: "RightHand_5",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["upperarm", "lowerarm", "forearm", "hand", "arm"],
  },

  // Left metacarpal chains
  LeftIndexMetacarpal: {
    name: "LeftIndexMetacarpal",
    description: "Left index metacarpal",
    startRole: "LeftIndexMetacarpal",
    endRole: "LeftIndexMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["index", "metacarpal"],
  },
  LeftMiddleMetacarpal: {
    name: "LeftMiddleMetacarpal",
    description: "Left middle metacarpal",
    startRole: "LeftMiddleMetacarpal",
    endRole: "LeftMiddleMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["middle", "metacarpal"],
  },
  LeftRingMetacarpal: {
    name: "LeftRingMetacarpal",
    description: "Left ring metacarpal",
    startRole: "LeftRingMetacarpal",
    endRole: "LeftRingMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["ring", "metacarpal"],
  },
  LeftPinkyMetacarpal: {
    name: "LeftPinkyMetacarpal",
    description: "Left pinky metacarpal",
    startRole: "LeftPinkyMetacarpal",
    endRole: "LeftPinkyMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["pinky", "little", "metacarpal"],
  },

  // Left finger chains
  LeftThumb: {
    name: "LeftThumb",
    description: "Left thumb finger",
    startRole: "LeftThumb1",
    endRole: "LeftThumb3",
    ikGoal: null,
    minBones: 2,
    maxBones: 4,
    detectionHints: ["thumb"],
  },
  LeftIndex: {
    name: "LeftIndex",
    description: "Left index finger",
    startRole: "LeftIndex1",
    endRole: "LeftIndex3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["index"],
  },
  LeftMiddle: {
    name: "LeftMiddle",
    description: "Left middle finger",
    startRole: "LeftMiddle1",
    endRole: "LeftMiddle3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["middle"],
  },
  LeftRing: {
    name: "LeftRing",
    description: "Left ring finger",
    startRole: "LeftRing1",
    endRole: "LeftRing3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["ring"],
  },
  LeftPinky: {
    name: "LeftPinky",
    description: "Left pinky finger",
    startRole: "LeftPinky1",
    endRole: "LeftPinky3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["pinky", "little"],
  },

  // Right metacarpal chains
  RightIndexMetacarpal: {
    name: "RightIndexMetacarpal",
    description: "Right index metacarpal",
    startRole: "RightIndexMetacarpal",
    endRole: "RightIndexMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["index", "metacarpal"],
  },
  RightMiddleMetacarpal: {
    name: "RightMiddleMetacarpal",
    description: "Right middle metacarpal",
    startRole: "RightMiddleMetacarpal",
    endRole: "RightMiddleMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["middle", "metacarpal"],
  },
  RightRingMetacarpal: {
    name: "RightRingMetacarpal",
    description: "Right ring metacarpal",
    startRole: "RightRingMetacarpal",
    endRole: "RightRingMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["ring", "metacarpal"],
  },
  RightPinkyMetacarpal: {
    name: "RightPinkyMetacarpal",
    description: "Right pinky metacarpal",
    startRole: "RightPinkyMetacarpal",
    endRole: "RightPinkyMetacarpal",
    ikGoal: null,
    minBones: 1,
    maxBones: 1,
    detectionHints: ["pinky", "little", "metacarpal"],
  },

  // Right finger chains
  RightThumb: {
    name: "RightThumb",
    description: "Right thumb finger",
    startRole: "RightThumb1",
    endRole: "RightThumb3",
    ikGoal: null,
    minBones: 2,
    maxBones: 4,
    detectionHints: ["thumb"],
  },
  RightIndex: {
    name: "RightIndex",
    description: "Right index finger",
    startRole: "RightIndex1",
    endRole: "RightIndex3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["index"],
  },
  RightMiddle: {
    name: "RightMiddle",
    description: "Right middle finger",
    startRole: "RightMiddle1",
    endRole: "RightMiddle3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["middle"],
  },
  RightRing: {
    name: "RightRing",
    description: "Right ring finger",
    startRole: "RightRing1",
    endRole: "RightRing3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["ring"],
  },
  RightPinky: {
    name: "RightPinky",
    description: "Right pinky finger",
    startRole: "RightPinky1",
    endRole: "RightPinky3",
    ikGoal: null,
    minBones: 3,
    maxBones: 4,
    detectionHints: ["pinky", "little"],
  },

  // Appendage chains
  Tail: {
    name: "Tail",
    description: "Tail chain from pelvis/spine base",
    startRole: "TailStart",
    endRole: "TailEnd",
    ikGoal: null,
    minBones: 3,
    maxBones: 20,
    detectionHints: [...APPENDAGE_HINTS.Tail],
  },
  Tentacle: {
    name: "Tentacle",
    description: "Tentacle chain",
    startRole: "TentacleStart",
    endRole: "TentacleEnd",
    ikGoal: null,
    minBones: 3,
    maxBones: 20,
    detectionHints: [...APPENDAGE_HINTS.Tentacle],
  },
  Antennae: {
    name: "Antennae",
    description: "Antennae chain",
    startRole: "AntennaeStart",
    endRole: "AntennaeEnd",
    ikGoal: null,
    minBones: 2,
    maxBones: 10,
    detectionHints: [...APPENDAGE_HINTS.Antennae],
  },
};

export const CHAIN_NAMES = Object.keys(CHAIN_DEFINITIONS) as readonly string[];

// =============================================================================
// Hints
// =============================================================================

export function getBaseRole(role: string): string {
  let base = role;
  if (base.startsWith("Left")) base = base.slice(4);
  else if (base.startsWith("Right")) base = base.slice(5);

  if (base.length > 0 && base[base.length - 1] >= "0" && base[base.length - 1] <= "9") {
    base = base.slice(0, -1);
  }

  return base;
}

export function getExpectedSide(role: string): string | null {
  if (role.startsWith("Left")) return "Left";
  if (role.startsWith("Right")) return "Right";
  return null;
}

// =============================================================================
// Topology
// =============================================================================

export const EXPECTED_CHILD_COUNTS: Record<string, [number, number]> = {
  Root: [1, 1],
  Pelvis: [2, 4],
  Spine: [1, 3],
  Chest: [2, 4],
  Neck: [1, 2],
  Head: [0, 5],
  LeftClavicle: [1, 2],
  RightClavicle: [1, 2],
  LeftUpperArm: [1, 3],
  LeftLowerArm: [1, 3],
  LeftHand: [4, 6],
  RightUpperArm: [1, 3],
  RightLowerArm: [1, 3],
  RightHand: [4, 6],
  LeftUpperLeg: [1, 3],
  LeftLowerLeg: [1, 2],
  LeftHock: [1, 2],
  LeftFoot: [0, 2],
  LeftToe: [0, 1],
  RightUpperLeg: [1, 3],
  RightLowerLeg: [1, 2],
  RightHock: [1, 2],
  RightFoot: [0, 2],
  RightToe: [0, 1],
  LeftClavicle_2: [1, 2],
  RightClavicle_2: [1, 2],
  LeftUpperArm_2: [1, 3],
  LeftLowerArm_2: [1, 3],
  LeftHand_2: [4, 6],
  RightUpperArm_2: [1, 3],
  RightLowerArm_2: [1, 3],
  RightHand_2: [4, 6],
  LeftClavicle_3: [1, 2],
  RightClavicle_3: [1, 2],
  LeftUpperArm_3: [1, 3],
  LeftLowerArm_3: [1, 3],
  LeftHand_3: [4, 6],
  RightUpperArm_3: [1, 3],
  RightLowerArm_3: [1, 3],
  RightHand_3: [4, 6],
  LeftClavicle_4: [1, 2],
  RightClavicle_4: [1, 2],
  LeftUpperArm_4: [1, 3],
  LeftLowerArm_4: [1, 3],
  LeftHand_4: [4, 6],
  RightUpperArm_4: [1, 3],
  RightLowerArm_4: [1, 3],
  RightHand_4: [4, 6],
  LeftClavicle_5: [1, 2],
  RightClavicle_5: [1, 2],
  LeftUpperArm_5: [1, 3],
  LeftLowerArm_5: [1, 3],
  LeftHand_5: [4, 6],
  RightUpperArm_5: [1, 3],
  RightLowerArm_5: [1, 3],
  RightHand_5: [4, 6],
};

export const EXPECTED_DEPTHS: Record<string, [number, number]> = {
  Root: [0, 0],
  Pelvis: [1, 2],
  Spine: [2, 5],
  Chest: [3, 6],
  Neck: [4, 8],
  Head: [5, 9],
  LeftClavicle: [4, 7],
  RightClavicle: [4, 7],
  LeftUpperArm: [5, 8],
  LeftLowerArm: [6, 9],
  LeftHand: [7, 11],
  RightUpperArm: [5, 8],
  RightLowerArm: [6, 9],
  RightHand: [7, 11],
  LeftUpperLeg: [2, 4],
  LeftLowerLeg: [3, 5],
  LeftHock: [4, 6],
  LeftFoot: [4, 7],
  LeftToe: [5, 8],
  RightUpperLeg: [2, 4],
  RightLowerLeg: [3, 5],
  RightHock: [4, 6],
  RightFoot: [4, 7],
  RightToe: [5, 8],
  LeftClavicle_2: [3, 7],
  RightClavicle_2: [3, 7],
  LeftUpperArm_2: [4, 8],
  LeftLowerArm_2: [5, 9],
  LeftHand_2: [6, 11],
  RightUpperArm_2: [4, 8],
  RightLowerArm_2: [5, 9],
  RightHand_2: [6, 11],
  LeftClavicle_3: [3, 7],
  RightClavicle_3: [3, 7],
  LeftUpperArm_3: [4, 8],
  LeftLowerArm_3: [5, 9],
  LeftHand_3: [6, 11],
  RightUpperArm_3: [4, 8],
  RightLowerArm_3: [5, 9],
  RightHand_3: [6, 11],
  LeftClavicle_4: [3, 7],
  RightClavicle_4: [3, 7],
  LeftUpperArm_4: [4, 8],
  LeftLowerArm_4: [5, 9],
  LeftHand_4: [6, 11],
  RightUpperArm_4: [4, 8],
  RightLowerArm_4: [5, 9],
  RightHand_4: [6, 11],
  LeftClavicle_5: [3, 7],
  RightClavicle_5: [3, 7],
  LeftUpperArm_5: [4, 8],
  LeftLowerArm_5: [5, 9],
  LeftHand_5: [6, 11],
  RightUpperArm_5: [4, 8],
  RightLowerArm_5: [5, 9],
  RightHand_5: [6, 11],
};

// =============================================================================
// Helpers
// =============================================================================

export function getChainDefinition(chainName: string): ChainSpec | undefined {
  return CHAIN_DEFINITIONS[chainName];
}

export function getFingerChains(side: string): string[] {
  return FINGER_ROLE_NAMES.flatMap((finger) => [
    ...(finger === "Thumb" ? [] : [`${side}${finger}Metacarpal`]),
    `${side}${finger}`,
  ]);
}

export function getFingerRoleNamesForChain(
  chainName: string,
  boneCount: number,
): string[] {
  if (chainName.includes('Metacarpal')) {
    return [chainName];
  }

  const maxFingerRoles = Math.min(boneCount, 3);
  const iterativeSeparator = /_\d+$/.test(chainName) ? '_' : '';
  return Array.from(
    { length: maxFingerRoles },
    (_, index) => `${chainName}${iterativeSeparator}${index + 1}`,
  );
}

export function getAllChainNames(): string[] {
  return Object.keys(CHAIN_DEFINITIONS);
}
